import { Link } from 'react-router-dom'
import { FileText, Eye, Star, Calendar, User, Building2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { LibraryDocument } from '@/services/libraryService'

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  artigo: 'Artigo',
  manual: 'Manual',
  boletim: 'Boletim',
  relatorio: 'Relatório',
  tese: 'Tese',
  guia: 'Guia',
}

interface DocumentCardProps {
  document: LibraryDocument
  className?: string
}

export function DocumentCard({ document: doc, className }: DocumentCardProps) {
  const category = doc.taxonomies?.find(t => t.type === 'library_category')
  const tags = doc.taxonomies?.filter(t => t.type === 'library_tag') || []

  return (
    <Link
      to={`/biblioteca/${doc.slug}`}
      className={cn(
        "block card hover:shadow-md transition-all duration-200 group overflow-hidden",
        className
      )}
    >
      <article>
        {/* Cover / Icon area */}
        <div className="bg-gradient-to-br from-brand-primary/10 to-brand-accent/10 h-40 flex items-center justify-center relative overflow-hidden">
          <FileText
            className="h-16 w-16 text-brand-primary/40 group-hover:scale-110 transition-transform duration-300"
            strokeWidth={1}
          />
          {doc.featured && (
            <div className="absolute top-3 right-3 bg-amber-500 text-white p-1.5 rounded-lg">
              <Star className="h-3.5 w-3.5 fill-white" />
            </div>
          )}
          {doc.page_count > 0 && (
            <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md">
              {doc.page_count} pág.
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Category + Type */}
          <div className="flex items-center gap-2 mb-3">
            {category && (
              <span className="inline-block px-2.5 py-1 text-xs font-medium bg-brand-primary-light text-brand-primary rounded-lg">
                {category.name}
              </span>
            )}
            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-brand-surface-alt text-brand-text-muted rounded-md">
              {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-brand-text mb-2 line-clamp-2 group-hover:text-brand-primary transition-colors">
            {doc.title}
          </h3>

          {/* Summary */}
          {doc.summary && (
            <p className="text-brand-text-secondary text-sm mb-3 line-clamp-2">
              {doc.summary}
            </p>
          )}

          {/* Author + Source */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-brand-text-muted mb-3">
            {doc.author_name && (
              <span className="flex items-center gap-1 truncate max-w-[140px]">
                <User className="w-3 h-3 flex-shrink-0" />
                {doc.author_name}
              </span>
            )}
            {doc.source && (
              <span className="flex items-center gap-1 truncate max-w-[140px]">
                <Building2 className="w-3 h-3 flex-shrink-0" />
                {doc.source}
              </span>
            )}
            {doc.publication_year && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {doc.publication_year}
              </span>
            )}
          </div>

          {/* Footer: tags + views */}
          <div className="flex items-center justify-between pt-3 border-t border-brand-border">
            <div className="flex gap-1 overflow-hidden">
              {tags.slice(0, 2).map(tag => (
                <span
                  key={tag.id}
                  className="px-1.5 py-0.5 text-xs bg-brand-surface-alt text-brand-text-muted rounded-md truncate max-w-[80px]"
                >
                  {tag.name}
                </span>
              ))}
              {tags.length > 2 && (
                <span className="px-1.5 py-0.5 text-xs bg-brand-surface-alt text-brand-text-muted rounded-md">
                  +{tags.length - 2}
                </span>
              )}
            </div>
            <span className="flex items-center gap-1 text-xs text-brand-text-muted">
              <Eye className="w-3 h-3" />
              {doc.view_count}
            </span>
          </div>
        </div>
      </article>
    </Link>
  )
}
